/**
 * TranslationSettings: 자막/번역 설정 패널
 *
 * 마크업: translate_2.html `.rtcTranslationSideSet` 기반
 * 6가지 설정 항목:
 * 1. 자막 표시 ON/OFF
 * 2. 번역 자막 ON/OFF
 * 3. 자동언어인식 ON/OFF
 * 4. 번역 검증 ON/OFF (disabled)
 * 5. 자막/번역 언어 드롭다운
 * 6. 자막 글꼴 크기 드롭다운
 *
 * 기획 다이얼로그 케이스:
 * - Case 1: 자막/번역 ON 시 내 음성인식 OFF + 다른참석자 1명 이상 ON → "음성 인식을 활성화할까요?"
 * - Case 2: 자막/번역 ON 시 모든 참석자 음성인식 OFF → "자막 표시 불가"
 * - 크레딧 소진: 일반 사용자 vs 마스터(서비스단 masterUsers 기준) 분기
 */
import React, { useState, useRef, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import {
  translationActions,
  TRANSLATION_LANGUAGES,
  FONT_SIZE_OPTIONS,
  CREDIT_THRESHOLD,
  type TranslationLanguage,
  type FontSizeOption,
} from '@/store/slices/translationSlice';
import { voiceNoteActions } from '@/store/slices/voiceNoteSlice';
import { captionActions } from '@/store/slices/captionSlice';
import { uiActions } from '@/store/slices/uiSlice';
import { toastActions } from '@/store/slices/toastSlice';
import { getConferenceManager } from '@/conference/conferenceManagerRef';
import { isMasterUser } from '@/store/slices/masterSlice';
import { CONFERENCE_COMMANDS } from '@/conference/commands';
import { SYSTEM_MSG, saveMessageToDB } from '@/conference/systemMessages';
import apiConfig from '@/api/config';
import { getMyCredit, getRoomCredit } from '@/api/translateAPI';
import LUXDialog from '@/components/Dialog/LUXDialog';

const TranslationSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const translation = useAppSelector((s) => s.translation);
  const local = useAppSelector((s) => s.local);
  const voiceNote = useAppSelector((s) => s.voiceNote);
  const caption = useAppSelector((s) => s.caption);
  const masterUsers = useAppSelector((s) => s.master.masterUsers);
  // 서비스단 마스터 여부 — Jitsi moderator role은 회의 정책(전원 moderator)과 무관하게 부여될 수 있으므로 사용 금지
  const isLocalMaster = isMasterUser(masterUsers, local.wehagoId);

  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [fontDropdownOpen, setFontDropdownOpen] = useState(false);
  const langDropdownRef = useRef<HTMLDivElement>(null);
  const fontDropdownRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(e.target as Node)) {
        setLangDropdownOpen(false);
      }
      if (fontDropdownRef.current && !fontDropdownRef.current.contains(e.target as Node)) {
        setFontDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ─── 음성인식 활성화 헬퍼 ───────────────────────────────────────────────

  const activateMySTT = () => {
    dispatch(captionActions.setEnabled(true));
    dispatch(uiActions.setCaptionEnabled(true));
    dispatch(voiceNoteActions.setEnabled(true));
    // CHANGE_SPEECH_BUTTON 커맨드 브로드캐스트
    const mgr = getConferenceManager();
    const isJigasiActive = caption.isTranscriberJoined
      || caption.isTranscriberDialPending
      || Boolean(mgr?.getConnector()?.isJigasiTranscriberActive());
    if (!isJigasiActive) {
      mgr?.sendCommand(CONFERENCE_COMMANDS.CHANGE_SPEECH_BUTTON, {
        value: local.jitsiId,
        attributes: { isSpeechStart: 'true', reduxStartPerson: local.jitsiId },
      });
    }
  };

  /** 다른 참여자 중 STT 활성화된 사람이 있는지 */
  const hasOtherSttParticipant = Object.values(voiceNote.sttParticipants).some((v) => v === true);

  /**
   * 자막/번역 ON 시 음성인식 상태 체크 (기획 다이얼로그)
   * @returns true → 그대로 진행 / false → 다이얼로그 표시 (비동기 처리)
   */
  const checkSttAndProceed = (onProceed: () => void): boolean => {
    const mySttOn = caption.isEnabled || voiceNote.isEnabled;

    if (mySttOn) {
      // 내 STT가 ON → 그대로 진행
      return true;
    }

    if (hasOtherSttParticipant) {
      // Case 1: 내 STT OFF, 다른 참석자 1명 이상 ON
      // → "음성 인식을 활성화할까요?" (question)
      // 취소해도 자막/번역은 켜짐 (다른 참석자의 STT로만 노출)
      LUXDialog.confirm(
        '내 음성 인식이 꺼져있어 다른 참석자에게는\n내 발언이 자막으로 보이지 않습니다.',
        {
          type: 'question',
          title: '음성 인식을 활성화할까요?',
          submitLabel: '음성인식활성화',
          cancelLabel: '취소',
          callback: () => {
            activateMySTT();
            onProceed();
          },
          callbackError: () => {
            // 취소해도 자막/번역은 ON (다른 참석자 STT로만 자막 노출)
            onProceed();
          },
        }
      );
      return false;
    }

    // Case 2: 모든 참석자 STT OFF
    // → "자막 표시 불가" (warning)
    // 취소하면 자막/번역 ON 하지 않음
    LUXDialog.confirm(
      '자막을 보려면 본인 또는 다른 참석자가\n음성 인식을 활성화해야 합니다.',
      {
        type: 'warning',
        title: '자막 표시 불가',
        submitLabel: '음성인식 활성화',
        cancelLabel: '취소',
        callback: () => {
          activateMySTT();
          onProceed();
        },
        callbackError: () => {
          // 취소 → 자막/번역 ON 안 함
        },
      }
    );
    return false;
  };

  // ─── 핸들러 ─────────────────────────────────────────────────────────────

  const handleCaptionToggle = () => {
    const nextEnabled = !translation.isCaptionEnabled;

    if (nextEnabled) {
      // ON 시 음성인식 상태 체크
      const proceed = checkSttAndProceed(() => {
        dispatch(translationActions.setCaptionEnabled(true));
        dispatch(toastActions.showSnackbar({ type: 'success', message: '화면에 자막을 표시합니다.' }));
        // 자막 시작 시스템 메시지 (사용자한테만 / 자막 히스토리)
        dispatch(translationActions.addSystemMessage('caption_started'));
        saveMessageToDB(SYSTEM_MSG.SUB_MODE_OFF, 'system');
      });
      if (!proceed) return; // 다이얼로그 표시됨 → 콜백에서 처리
    }

    dispatch(translationActions.setCaptionEnabled(nextEnabled));

    if (nextEnabled) {
      dispatch(toastActions.showSnackbar({ type: 'success', message: '화면에 자막을 표시합니다.' }));
      dispatch(translationActions.addSystemMessage('caption_started'));
      saveMessageToDB(SYSTEM_MSG.SUB_MODE_OFF, 'system');
    } else {
      dispatch(toastActions.showSnackbar({ type: 'success', message: '화면에 자막을 표시하지 않습니다.' }));
      dispatch(translationActions.addSystemMessage('caption_ended'));
      saveMessageToDB(SYSTEM_MSG.SUB_MODE_OFF, 'system');
    }
  };

  const handleTranslateToggle = async () => {
    const nextEnabled = !translation.isTranslateEnabled;

    if (nextEnabled) {
      // ── 1) 음성인식 상태 체크 ──
      const mySttOn = voiceNote.isEnabled;

      if (!mySttOn) {
        // 음성인식 OFF → 다이얼로그 표시 후 콜백에서 크레딧 체크 + 활성화 처리
        checkSttAndProceed(() => {
          doTranslateEnable();
        });
        return;
      }

      // ── 2) 크레딧 확인 후 활성화 ──
      await doTranslateEnable();
      return;
    }

    // OFF 처리
    dispatch(translationActions.setTranslateEnabled(false));
    dispatch(toastActions.showSnackbar({ type: 'success', message: '화면에 번역을 표시하지 않습니다.' }));
    broadcastTranslationStatus(false);
    // 번역 종료 시스템 메시지 (사용자한테만 / 자막 히스토리)
    dispatch(translationActions.addSystemMessage('translation_ended'));
    saveMessageToDB(SYSTEM_MSG.TRANS_MODE_OFF, 'system');
  };

  /** 번역 활성화 공통 로직 (크레딧 확인 + dispatch + 브로드캐스트) */
  const doTranslateEnable = async () => {
    const creditMode = local.creditMode;

    try {
      let credit: number;

      if (creditMode === 'USER') {
        // 세션 사용자 크레딧 조회 (GET /v2/chat/credit/me)
        const res = await getMyCredit();
        credit = res?.resultData?.total_credit ?? 0;
      } else {
        // 방 개설자 크레딧 조회 (GET /v2/chat/credit/room?roomId=)
        const res = await getRoomCredit(local.roomId);
        credit = res?.resultData?.total_credit ?? 0;
      }

      dispatch(translationActions.setTotalCredit(credit));

      if (credit < CREDIT_THRESHOLD) {
        dispatch(toastActions.showSnackbar({
          type: 'error',
          message: 'Credit이 부족하여 번역 기능을 사용할 수 없습니다.',
        }));
        return;
      }
    } catch {
      dispatch(toastActions.showSnackbar({
        type: 'error',
        message: '크레딧 조회에 실패했습니다. 다시 시도해주세요.',
      }));
      return;
    }

    dispatch(translationActions.setTranslateEnabled(true));
    dispatch(toastActions.showSnackbar({ type: 'success', message: '화면에 번역을 표시합니다.' }));
    broadcastTranslationStatus(true);
    // 번역 시작 시스템 메시지 (사용자한테만 / 자막 히스토리)
    dispatch(translationActions.addSystemMessage('translation_started'));
    saveMessageToDB(SYSTEM_MSG.TRANS_MODE_ON, 'system');
  };

  /** 번역 상태 브로드캐스트 */
  const broadcastTranslationStatus = (enabled: boolean) => {
    const mgr = getConferenceManager();
    mgr?.sendCommand(CONFERENCE_COMMANDS.TRANSLATION_TOGGLE_STATUS, {
      attributes: {
        userId: local.jitsiId,
        enabled: String(enabled),
        language: translation.language,
      },
    });
  };

  const broadcastWehagoIdentity = (options: {
    nationality?: TranslationLanguage;
    enableAutoLang?: boolean;
  } = {}) => {
    const mgr = getConferenceManager();
    const room = mgr?.getRoom();
    const jitsiId = local.jitsiId || room?.myUserId() || '';
    if (!jitsiId) return;

    const enableAutoLang = options.enableAutoLang ?? translation.isAutoLangEnabled;
    room?.setLocalParticipantProperty('enableAutoLang', String(enableAutoLang));
    mgr?.sendCommand(CONFERENCE_COMMANDS.WEHAGO_ID, {
      value: jitsiId,
      attributes: {
        nationality: options.nationality ?? translation.language,
        enableAutoLang: String(enableAutoLang),
      },
    });
  };

  const handleAutoLangToggle = () => {
    const nextEnabled = !translation.isAutoLangEnabled;
    dispatch(translationActions.setAutoLangEnabled(nextEnabled));
    broadcastWehagoIdentity({ enableAutoLang: nextEnabled });
  };

  const handleLanguageSelect = (code: TranslationLanguage) => {
    dispatch(translationActions.setLanguage(code));
    setLangDropdownOpen(false);

    // nationality 재브로드캐스트 → 번역 미사용 참여자의 국기 표시에 활용
    broadcastWehagoIdentity({ nationality: code });

    // 번역 활성화 상태에서 언어 변경 시 → 변경된 언어 브로드캐스트
    if (translation.isTranslateEnabled) {
      const mgr = getConferenceManager();
      mgr?.sendCommand(CONFERENCE_COMMANDS.TRANSLATION_TOGGLE_STATUS, {
        attributes: {
          userId: local.jitsiId,
          enabled: 'true',
          language: code,
        },
      });
    }
  };

  const handleCreditPurchaseClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const url = `${apiConfig.portalBaseUrl}/#/company/servicemanagement/oneaiusercreditmanager`;
    window.open(url, '_blank');
  };

  const handleFontSizeSelect = (size: FontSizeOption) => {
    dispatch(translationActions.setFontSize(size));
    setFontDropdownOpen(false);
  };

  const currentLangLabel =
    TRANSLATION_LANGUAGES.find((l) => l.code === translation.language)?.label ?? '한국어';

  return (
    <>
      <div className="rtcTranslationSideSet">
        {/* 1. 자막 표시 */}
        <div className="rtcTranslationSideSet__item">
          <div className="textBox textBox--icon1">
            <strong className="textBox__title">자막 표시</strong>
            <p className="textBox__desc">
              음성인식을 설정한 참여자들의 발언을
              <br />
              자막으로 제공합니다.
            </p>
          </div>
          <div className="optBox">
            <div className="common_onoff_switch small2 type_blue2">
              <label>
                <input
                  className="onoff_input"
                  type="checkbox"
                  checked={translation.isCaptionEnabled}
                  onChange={handleCaptionToggle}
                />
                <span className="switch_img" />
                <span className="label_text on" />
                <span className="label_text off" />
              </label>
            </div>
          </div>
        </div>

        {/* 2. 번역 자막 */}
        <div className="rtcTranslationSideSet__item">
          <div className="textBox textBox--icon2">
            <strong className="textBox__title">번역 자막</strong>
            <p className="textBox__desc">
              발언 내용을 원하는 언어로
              <br />
              실시간 번역하여 자막으로 제공합니다.
            </p>
          </div>
          <div className="optBox">
            <div className="common_onoff_switch small2 type_blue2">
              <label>
                <input
                  className="onoff_input"
                  type="checkbox"
                  checked={translation.isTranslateEnabled}
                  onChange={handleTranslateToggle}
                />
                <span className="switch_img" />
                <span className="label_text on" />
                <span className="label_text off" />
              </label>
            </div>
          </div>
        </div>

        {/* 3. 자동언어인식 */}
        <div className="rtcTranslationSideSet__item">
          <div className="textBox textBox--icon3">
            <strong className="textBox__title">자동언어인식</strong>
            <p className="textBox__desc">
              발화자의 언어를 자동으로 인식합니다.
            </p>
          </div>
          <div className="optBox">
            <div className="common_onoff_switch small2 type_blue2">
              <label>
                <input
                  className="onoff_input"
                  type="checkbox"
                  checked={translation.isAutoLangEnabled}
                  onChange={handleAutoLangToggle}
                />
                <span className="switch_img" />
                <span className="label_text on" />
                <span className="label_text off" />
              </label>
            </div>
          </div>
        </div>

        {/* 4. 번역 검증 (비활성) */}
        {/* <div className="rtcTranslationSideSet__item">
          <div className="textBox textBox--icon3">
            <strong className="textBox__title">번역 검증</strong>
            <p className="textBox__desc">
              내가 발언한 내용이 다른 참여자의 언어로
              <br />
              번역된 결과를 확인할 수 있습니다.
            </p>
          </div>
          <div className="optBox">
            <div className="common_onoff_switch small2 type_blue2">
              <label>
                <input
                  className="onoff_input"
                  type="checkbox"
                  checked={false}
                  disabled
                  readOnly
                />
                <span className="switch_img" />
                <span className="label_text on" />
                <span className="label_text off" />
              </label>
            </div>
          </div>
        </div> */}

        {/* 5. 자막/번역 언어 */}
        <div className="rtcTranslationSideSet__item">
          <div className="textBox textBox--icon4">
            <strong className="textBox__title">자막/번역언어</strong>
          </div>
          <div className="optBox" ref={langDropdownRef}>
            <button
              type="button"
              className="optBox__btn"
              onClick={() => setLangDropdownOpen((v) => !v)}
            >
              {currentLangLabel}
              <span className="lunacon lunacon--arrowRight-outlined" />
            </button>
            {langDropdownOpen && (
              <div className="common_component_dropdown2 notosans_font" style={{ display: 'block' }}>
                <ul className="common_component_dropdownList">
                  {TRANSLATION_LANGUAGES.map((lang) => (
                    <li key={lang.code}>
                      <button
                        type="button"
                        className={`common_component_dropdownBtn ellipsis${
                          translation.language === lang.code ? ' selected' : ''
                        }`}
                        onClick={() => handleLanguageSelect(lang.code)}
                      >
                        {lang.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* 6. 자막 글꼴 크기 */}
        <div className="rtcTranslationSideSet__item">
          <div className="textBox textBox--icon5">
            <strong className="textBox__title">자막 글꼴 크기</strong>
          </div>
          <div className="optBox" ref={fontDropdownRef}>
            <button
              type="button"
              className="optBox__btn"
              onClick={() => setFontDropdownOpen((v) => !v)}
            >
              {translation.fontSize}%
              <span className="lunacon lunacon--arrowRight-outlined" />
            </button>
            {fontDropdownOpen && (
              <div className="common_component_dropdown2 notosans_font" style={{ display: 'block' }}>
                <ul className="common_component_dropdownList">
                  {FONT_SIZE_OPTIONS.map((size) => (
                    <li key={size}>
                      <button
                        type="button"
                        className={`common_component_dropdownBtn ellipsis${
                          translation.fontSize === size ? ' selected' : ''
                        }`}
                        onClick={() => handleFontSizeSelect(size)}
                      >
                        {size}%
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 크레딧 소진 알림 — 일반 사용자 vs 마스터(방장) 분기 */}
      {!translation.creditAvailable && (
        !isLocalMaster ? (
          <a href="#" className="rtcTranslationSideInfo error" onClick={handleCreditPurchaseClick}>
            <p className="titleText">Credit이 모두 소진되어 번역이 중지되었습니다.</p>
            <p className="subText">
              추가 크레딧 구매하기
              <span className="lunacon lunacon--arrowRight-outlined" />
            </p>
          </a>
        ) : (
          <div className="rtcTranslationSideInfo error">
            <p className="titleText">Credit이 모두 소진되어 번역이 중지되었습니다.</p>
            <p className="subText">크레딧 추가 구매는 회사 관리자에게 문의해주세요.</p>
          </div>
        )
      )}

      {translation.creditAvailable && (
        <div className="rtcTranslationSideInfo">
          <p>
            *자막/번역 기능은 음성인식을 설정한 참여자들의 발언에만
            <br />
            기능을 제공하며 실시간 번역은 Credit이 차감됩니다.
          </p>
        </div>
      )}
    </>
  );
};

export default TranslationSettings;
